import { ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import { Payment } from '@vsolv/packages/payments/domain';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { TableColumn, TextColumn } from '@vsolv/vectors/table';
import { BatchPayment } from '@wsphere/warranties/domain';
import { PAYMENT_HISTORY_DETAIL_CONFIG } from '../../config/payment-history.config';
import { PaymentHistoryService } from '../../services';

function formatCents(cents: number | string | undefined) {
  return (Number(cents ?? 0) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-payment-history-detail-dialog',
  templateUrl: './payment-history-detail.dialog.html',
  standalone: false,
})
export class PaymentHistoryDetailDialog {
  private historySvc = inject(PaymentHistoryService);

  @ViewChild(DialogComponent) private dialog?: DialogComponent;

  protected item: BatchPayment.HistoryItem | null = null;
  protected warranties: BatchPayment.HistoryWarranty[] | null = null;
  protected error = false;

  protected readonly config = PAYMENT_HISTORY_DETAIL_CONFIG;
  protected readonly statusLabels = BatchPayment.HistoryStatusLabel;
  protected readonly partiallyRefunded = Payment.Status.PARTIALLY_REFUNDED;
  protected readonly partiallyRefundedLabel = BatchPayment.PartiallyRefundedLabel;

  columns: TableColumn<unknown>[] = [
    new TextColumn<BatchPayment.HistoryWarranty>(
      { header: PAYMENT_HISTORY_DETAIL_CONFIG.columns.warranty },
      warranty => ({
        text: warranty.contractNumber || '—',
        classes: 'font-medium',
      })
    ),

    new TextColumn<BatchPayment.HistoryWarranty>({ header: PAYMENT_HISTORY_DETAIL_CONFIG.columns.buyer }, warranty => ({
      text: warranty.customerName ?? '—',
    })),

    new TextColumn<BatchPayment.HistoryWarranty>(
      { header: PAYMENT_HISTORY_DETAIL_CONFIG.columns.coverage },
      warranty => ({
        text: warranty.planTitle ?? warranty.policyTitle ?? '—',
      })
    ),

    new TextColumn<BatchPayment.HistoryWarranty>(
      { header: PAYMENT_HISTORY_DETAIL_CONFIG.columns.netAmount, align: 'right', fitContent: true },
      warranty => ({
        text: formatCents(warranty.amountApplied),
        classes: 'font-medium',
      })
    ),
  ];

  // Skeleton matches the known warranty count so the dialog doesn't jump when the rows load
  protected get skeletonRows() {
    return Math.min(Math.max(this.item?.warrantyCount ?? 3, 1), 8);
  }

  // Bumped per open so a slow response for a previously opened batch can't overwrite this one
  private loadGeneration = 0;

  async open(item: BatchPayment.HistoryItem) {
    this.item = item;
    this.warranties = null;
    this.error = false;
    const generation = ++this.loadGeneration;

    this.dialog?.open();

    try {
      const warranties = await this.historySvc.listWarranties(item.id);
      if (generation === this.loadGeneration) this.warranties = warranties;
    } catch {
      if (generation === this.loadGeneration) this.error = true;
    }
  }

  close() {
    this.dialog?.close();
  }
}
